export type CashTenderSuggestionKind = "EXACT" | "CONVENIENT" | "LARGER";

export type CashTenderSuggestion = {
  amount: number;
  change: number;
  kind: CashTenderSuggestionKind;
};

export type CashTenderSuggestionInput = {
  amountDue: number;
  precision?: number;
  maxSuggestions?: number;
};

function clampPrecision(value: number | undefined) {
  if (!Number.isFinite(value)) return 2;
  return Math.max(0, Math.min(6, Math.trunc(value ?? 2)));
}

const THAI_CASH_STEPS_MAJOR = Object.freeze([10, 20, 50, 100, 500, 1000]);

function nextStrictMultipleMinor(value: number, step: number) {
  if (step <= 0) return value;
  return (Math.floor(value / step) + 1) * step;
}

/**
 * Deterministic, Local-first tender suggestion resolver.
 *
 * The resolver intentionally derives convenient cash steps from the payable
 * magnitude instead of carrying Tenant / Branch / Provider / device-specific
 * banknote tables in POS code. A future Published Tender Presentation Policy
 * can replace/extend the candidate input without changing the cash commit path.
 */
export function resolveCashTenderSuggestions({
  amountDue,
  precision = 2,
  maxSuggestions = 6,
}: CashTenderSuggestionInput): CashTenderSuggestion[] {
  if (!Number.isFinite(amountDue) || amountDue < 0) return [];

  const safePrecision = clampPrecision(precision);
  const factor = 10 ** safePrecision;
  const dueMinor = Math.round(amountDue * factor);
  const limit = Math.max(1, Math.min(8, Math.trunc(maxSuggestions)));
  const suggestions: CashTenderSuggestion[] = [];
  const seen = new Set<number>();

  function push(amountMinor: number, kind: CashTenderSuggestionKind) {
    if (suggestions.length >= limit || amountMinor < dueMinor || seen.has(amountMinor)) return;
    seen.add(amountMinor);
    suggestions.push({
      amount: amountMinor / factor,
      change: (amountMinor - dueMinor) / factor,
      kind,
    });
  }

  if (dueMinor === 0) return suggestions;

  // Exact cash is already the default value in the tender field. Suggestions
  // therefore show only useful amounts above the payable. The Thai cash ladder
  // covers ฿10/20/50 coins-notes and ฿100/500/1,000 notes; strict rounding also
  // produces common combinations such as ฿80 for a ฿70 bill and ฿380/400 for
  // a ฿375 bill.
  THAI_CASH_STEPS_MAJOR.forEach((stepMajor) => {
    const stepMinor = Math.max(1, Math.round(stepMajor * factor));
    const candidate = nextStrictMultipleMinor(dueMinor, stepMinor);
    push(candidate, stepMajor <= 100 ? "CONVENIENT" : "LARGER");
  });

  return suggestions;
}

export type CashTenderKey =
  | "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9"
  | "00" | "." | "BACKSPACE" | "CLEAR";

export function applyCashTenderKey(currentValue: string, key: CashTenderKey, precision = 2) {
  const safePrecision = clampPrecision(precision);
  const current = currentValue.trim();

  if (key === "CLEAR") return "";
  if (key === "BACKSPACE") return current.slice(0, -1);
  if (key === ".") {
    if (safePrecision === 0 || current.includes(".")) return current;
    return current ? `${current}.` : "0.";
  }

  const decimalIndex = current.indexOf(".");
  if (decimalIndex >= 0) {
    const decimalLength = current.length - decimalIndex - 1;
    if (decimalLength + key.length > safePrecision) return current;
  }

  if (!current) return key === "00" ? "0" : key;
  if (current === "0" && !current.includes(".")) {
    if (key === "00" || key === "0") return "0";
    return key;
  }

  return `${current}${key}`;
}
